import React from "react";
import { useI18n } from "@/i18n/I18nContext";
import { WaxSeal } from "@/components/Illustrations";
import CinematicPlate from "@/components/CinematicPlate";
import ChapterRow, { ChapterCta, ManuscriptNote, ChapterBridge } from "./ChapterRow";

const HERITAGE_PLATE =
  "https://customer-assets.emergentagent.com/job_heritage-blend-4/artifacts/gv8oe0lq_Artboard%201.png";

/**
 * Chapter I — HERITAGE
 * Question: Who are the Ramirez? What is their legacy?
 */
export default function ChapterHeritage() {
  const { t, lang } = useI18n();
  const pillars = t("standard.pillars");

  const epigraphEN = (
    <>
      <span className="text-tobacco/85">Who are the Ramírez?</span>
      <br />A Dominican family. Three generations of tobacco.
    </>
  );
  const epigraphES = (
    <>
      <span className="text-tobacco/85">¿Quiénes son los Ramírez?</span>
      <br />Una familia dominicana. Tres generaciones de tabaco.
    </>
  );

  return (
    <>
      <ChapterRow
        id="heritage"
        testid="chapter-heritage"
        numeral="I"
        name="WHO WE ARE"
        chapterLabel={lang === "es" ? "Capítulo I — Quiénes Somos" : "Chapter I — Who We Are"}
        epigraph={lang === "es" ? epigraphES : epigraphEN}
        headline={t("legacy.title")}
        body={
          <>
            <p className="text-tobacco/85 text-[15.5px] md:text-[15px] leading-[1.7] font-normal">
              {t("legacy.body_1")}
            </p>
            <p className="text-tobacco/65 italic font-display text-[16px] leading-snug pt-2">
              {lang === "es"
                ? "“Tres generaciones, una sola disciplina.”"
                : "“Three generations, one discipline.”"}
            </p>
          </>
        }
        cta={
          <ChapterCta
            testid="heritage-cta"
            onClick={() => document.getElementById("land")?.scrollIntoView({ behavior: "smooth" })}
          >
            {lang === "es" ? "Continuar al Legado" : "Continue to The Land"} →
          </ChapterCta>
        }
        illustration={
          <figure className="w-full">
            <img
              src={HERITAGE_PLATE}
              alt={
                lang === "es"
                  ? "Agricultores tabacaleros — un tabacalero llevando hojas frente a la casa de curado, dos tabacaleros trabajando el campo"
                  : "Tobacco farmers — a worker carrying leaves before the curing barn, two farmers working the field"
              }
              data-testid="heritage-plate"
              loading="lazy"
              decoding="async"
              className="w-full h-auto block select-none pointer-events-none mx-auto"
              style={{
                // ~15–20% larger than the previous landscape plate, with
                // generous breathing room — used 1:1 (transparent PNG, no
                // filters, no blend modes, no contrast).
                maxHeight: "clamp(420px, 56vw, 540px)",
                objectFit: "contain",
              }}
            />
            <figcaption className="mt-4 flex items-baseline justify-between font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55">
              <span>Plate · II</span>
              <span className="hidden md:inline italic font-display text-[13px] text-tobacco/70 normal-case tracking-normal">
                {lang === "es"
                  ? "Tabacaleros — Las Matas de Farfán, c. 1956"
                  : "Tobacco farmers — Las Matas de Farfán, c. 1956"}
              </span>
              <span>Folio · 002</span>
            </figcaption>
          </figure>
        }
        meta={
          <ManuscriptNote
            label={lang === "es" ? "Anotaciones" : "Annotations"}
            items={[
              { k: lang === "es" ? "Fundado" : "Established", v: "MCMXLVIII · 1948" },
              { k: lang === "es" ? "Generaciones" : "Generations", v: "III" },
              { k: lang === "es" ? "Familias" : "Families", v: "468+" },
              { k: lang === "es" ? "Años" : "Years", v: "78" },
            ]}
          />
        }
        divider={false}
      />

      {/* Manifesto strip — folds The Ramirez Standard inline */}
      <section
        data-testid="heritage-standard-strip"
        className="relative bg-pearl text-tobacco py-12 md:py-14 border-t border-tobacco/10"
      >
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-6 md:gap-10 items-start">
          <div className="col-span-12 md:col-span-3 flex items-start gap-4">
            <WaxSeal className="w-14 h-14 md:w-16 md:h-16 shrink-0 opacity-90 mt-1" />
            <div>
              <div className="font-mono text-[9px] tracking-widest2 uppercase text-tobacco/55">
                {t("standard.eyebrow")}
              </div>
              <div className="mt-1 font-display italic text-[19px] md:text-[21px] leading-tight text-tobacco">
                {t("standard.title")}
              </div>
              <p className="mt-3 text-tobacco/65 text-[12.5px] leading-relaxed max-w-[28ch]">
                {lang === "es"
                  ? "Cuatro principios que sostienen cada hoja, cada barril, cada caja."
                  : "Four principles that hold every leaf, every barrel, every box."}
              </p>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-4">
            {pillars.map((p, i) => (
              <div key={p.k} data-testid={`pillar-${i}`} className="border-l border-tobacco/20 pl-3">
                <div className="font-mono text-[8.5px] tracking-widest2 uppercase text-tobacco/55">
                  0{i + 1} / IV
                </div>
                <div className="mt-1 font-display italic text-[17px] md:text-[19px] leading-tight text-tobacco">
                  {p.k}
                </div>
                <p className="mt-1.5 text-tobacco/70 text-[12.5px] leading-snug">{p.d}</p>
              </div>
            ))}
          </div>

          <div className="hidden md:block col-span-2 text-right">
            <div className="font-mono text-[10px] tracking-widest2 text-tobacco/55">I · cont.</div>
          </div>
        </div>
      </section>

      {/* Cinematic plate — emotional centerpiece between Heritage and The Land */}
      <CinematicPlate
        testid="cinematic-plate"
        height="h-[68vh] md:h-[78vh]"
        caption={{
          left: "Plate · Cinematica",
          center:
            lang === "es"
              ? "Tabaco dominicano. Tradición familiar."
              : "Dominican tobacco. Family tradition.",
          right: "MMXLVIII",
        }}
      />

      {/* Closing bridge into Chapter II */}
      <section className="relative bg-pearl text-tobacco border-t border-tobacco/10">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 py-8 md:py-10">
          <div className="border-t border-tobacco/15 pt-5 flex flex-wrap items-baseline justify-between gap-3 font-mono text-[10px] tracking-widest2 uppercase text-tobacco/55">
            <ChapterBridge
              left={
                lang === "es"
                  ? "Pero ningún legado nace en una casa — nace en la tierra."
                  : "But no legacy is born in a house — it is born in the land."
              }
              right={lang === "es" ? "Capítulo II · La Tierra" : "Chapter II · The Land"}
              testid="bridge-i-to-ii"
              onClick={() => document.getElementById("land")?.scrollIntoView({ behavior: "smooth" })}
            />
          </div>
        </div>
      </section>
    </>
  );
}
